import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Database, GitMerge, Calculator, ShieldCheck, FileText, Landmark,
  Brain, Plug, Leaf, RefreshCw, Sparkles, Activity as ActivityIcon,
  Target, GitBranch, Droplets, BarChart3,
} from 'lucide-react';
import {
  ResponsiveContainer, AreaChart, Area, PieChart, Pie, Cell, Legend,
  XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import { API } from '../App';
import TabNavigation from '../components/TabNavigation';
import { useSite } from '../context/SiteContext';
import { useAlert } from '../context/AlertContext';
import MrvKpiCard from '../components/mrv/MrvKpiCard';

// MRV section panels (M1-M8 module structure mirrors saarv-mrv-beta)
import ActivityPanel from './mrv/ActivityPanel';            // M1 Data Ingestion
import StandardisationPanel from './mrv/StandardisationPanel'; // M2 Standardisation
import EmissionsPanel from './mrv/EmissionsPanel';          // M3 Emissions Calc
import AuditPanel from './mrv/AuditPanel';                  // M4 Audit & Integrity
import ReportsPanel from './mrv/ReportsPanel';              // M5 MRV Reporting
import CreditsPanel from './mrv/CreditsPanel';              // M6 Carbon Ledger
import DecisionPanel from './mrv/DecisionPanel';            // M7 Decision Intel
import IntegrationsPanel from './mrv/IntegrationsPanel';    // M8 Integrations

/**
 * MRV (Monitoring, Reporting, Verification) Hub
 *
 * Single-route hub with internal tab navigation. Each tab is a panel that
 * renders a slice of MRV functionality (activity, standardisation, emissions,
 * reports, registry, credits, goals, scenarios, integrations).
 *
 * Reads operational data from OIM via the mrv_v_activity_data view — so OIM
 * energy/production/water/waste streams show up automatically without
 * separate ingestion.
 */

// M1-M8 module structure mirrors saarv-mrv-beta. Each tab is a "module";
// dashboard is the unprefixed first tab.
const MRV_TABS = [
  { id: 'dashboard',       label: 'Dashboard',         num: null, Icon: BarChart3 },
  { id: 'activity',        label: 'Data Ingestion',    num: 'M1', Icon: Database },
  { id: 'standardisation', label: 'Standardisation',   num: 'M2', Icon: GitMerge },
  { id: 'emissions',       label: 'Emissions Calc',    num: 'M3', Icon: Calculator },
  { id: 'audit',           label: 'Audit & Integrity', num: 'M4', Icon: ShieldCheck },
  { id: 'reports',         label: 'MRV Reporting',     num: 'M5', Icon: FileText },
  { id: 'credits',         label: 'Carbon Ledger',     num: 'M6', Icon: Landmark },
  { id: 'decision',        label: 'Decision Intel',    num: 'M7', Icon: Brain },
  { id: 'integrations',    label: 'Integrations',      num: 'M8', Icon: Plug },
];

export default function MRVHub({ user, onLogout }) {
  const { activeSiteId } = useSite() || { activeSiteId: null };
  const { showError } = useAlert();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      // Site-scope the dashboard so its totals match every other MRV tab
      // (Activity Data, Emissions Calc) when a site is selected. No site
      // selected → tenant-wide totals (legacy behaviour).
      const qs = activeSiteId ? `?site_id=${activeSiteId}` : '';
      const res = await axios.get(`${API}/mrv/dashboard/stats${qs}`);
      setStats(res.data);
    } catch (e) {
      console.error('MRV dashboard fetch failed', e);
      const msg = e.response?.data?.detail || 'Failed to load MRV dashboard';
      showError(msg);
    } finally {
      setLoading(false);
    }
  }, [showError, activeSiteId]);

  useEffect(() => {
    if (activeTab === 'dashboard') {
      fetchDashboard();
    }
  }, [activeTab, fetchDashboard]);

  const renderPanel = () => {
    switch (activeTab) {
      case 'activity':         return <ActivityPanel siteId={activeSiteId} />;
      case 'standardisation':  return <StandardisationPanel siteId={activeSiteId} />;
      case 'emissions':        return <EmissionsPanel siteId={activeSiteId} />;
      case 'audit':            return <AuditPanel siteId={activeSiteId} />;
      case 'reports':          return <ReportsPanel siteId={activeSiteId} />;
      case 'credits':          return <CreditsPanel siteId={activeSiteId} />;
      case 'decision':         return <DecisionPanel siteId={activeSiteId} />;
      case 'integrations':     return <IntegrationsPanel />;
      case 'dashboard':
      default:
        return <DashboardPanel stats={stats} loading={loading} onRefresh={fetchDashboard} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <TabNavigation user={user} onLogout={onLogout} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <Leaf className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">MRV — Monitoring, Reporting, Verification</h1>
              <p className="text-sm text-gray-500">
                Emissions calculation, scope-1/2/3 reporting, registry submissions, carbon credits & decarbonisation pathways
              </p>
            </div>
          </div>
        </div>

        {/* M1-M8 module tabs (horizontal scroll on small screens) */}
        <div className="border-b border-gray-200 bg-white rounded-t-lg overflow-x-auto">
          <nav className="-mb-px flex gap-1 px-2" aria-label="MRV modules">
            {MRV_TABS.map(({ id, label, num, Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => setActiveTab(id)}
                className={`whitespace-nowrap py-3 px-3 inline-flex items-center gap-2 border-b-2 text-sm font-medium transition-colors ${
                  activeTab === id
                    ? 'border-emerald-600 text-emerald-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                data-testid={`mrv-tab-${id}`}
              >
                <Icon className="w-4 h-4" />
                {num && <span className="font-mono text-[10px] opacity-70">{num}</span>}
                <span>{label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Active panel */}
        <div className="bg-white border border-t-0 border-gray-200 rounded-b-lg p-6 min-h-[400px]">
          {renderPanel()}
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// Dashboard panel — renders inline because it's small + fully decoupled
// ===========================================================================

const SCOPE_COLORS = { scope_1: '#dc2626', scope_2: '#2563eb', scope_3: '#7c3aed' };

// Category colours + labels for the records-by-category donut. The underlying
// data_origin column on the view is an implementation detail; we surface it
// here as a neutral category label (Energy/Production/Water/Waste/Manual).
const ORIGIN_COLORS = {
  mrv_native: '#10b981',
  oim_energy_hourly: '#3b82f6',
  oim_production_hourly: '#22d3ee',
  oim_water_hourly: '#0ea5e9',
  oim_waste_hourly: '#a855f7',
};

const ORIGIN_LABEL = {
  mrv_native: 'Manual entry',
  oim_energy_hourly: 'Energy',
  oim_production_hourly: 'Production',
  oim_water_hourly: 'Water',
  oim_waste_hourly: 'Waste',
};

function DashboardPanel({ stats, loading, onRefresh }) {
  const a = stats?.activity || {};
  const c = stats?.credits || {};
  const byScope = (stats?.by_scope || []).filter(s => s.tco2e > 0);
  const monthly = (stats?.monthly_trend || []);
  const byOrigin = (stats?.by_origin || []).map(o => ({
    ...o,
    label: ORIGIN_LABEL[o.data_origin] || o.data_origin,
    color: ORIGIN_COLORS[o.data_origin] || '#9ca3af',
  }));

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Overview</h2>
        <button
          type="button"
          onClick={onRefresh}
          disabled={loading}
          className="text-sm text-blue-700 hover:text-blue-900 inline-flex items-center gap-1"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Top row — KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <MrvKpiCard
          title="Total tCO2e (calculated)"
          value={a.total_tco2e != null ? Number(a.total_tco2e).toFixed(2) : '—'}
          sub={`${Number(a.calculated || 0).toLocaleString()} of ${Number(a.total_records || 0).toLocaleString()} records calculated`}
          Icon={Leaf}
          accent="emerald"
          loading={loading}
        />
        <MrvKpiCard
          title="Pending calc"
          value={a.pending != null ? Number(a.pending).toLocaleString() : '—'}
          sub={a.no_factor ? `${Number(a.no_factor).toLocaleString()} no-factor records blocked` : 'records awaiting calc'}
          Icon={ActivityIcon}
          accent="amber"
          loading={loading}
        />
        <MrvKpiCard
          title="Total records"
          value={a.total_records != null ? Number(a.total_records).toLocaleString() : '—'}
          sub="across all activity types"
          Icon={GitBranch}
          accent="blue"
          loading={loading}
        />
        <MrvKpiCard
          title="Available credits"
          value={c.available != null ? Number(c.available).toFixed(0) + ' tCO2e' : '—'}
          sub={`${Number(c.retired || 0).toFixed(0)} tCO2e retired · ${Number(c.transferred || 0).toFixed(0)} transferred`}
          Icon={Droplets}
          accent="violet"
          loading={loading}
        />
      </div>

      {/* Mid row — Trend chart + Scope donut */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {/* Monthly trend */}
        <div className="lg:col-span-2 bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-baseline justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-700">Emissions trend — last 12 months</h3>
            <span className="text-xs text-gray-500">tCO2e</span>
          </div>
          {monthly.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={monthly}>
                <defs>
                  <linearGradient id="emissionGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.55}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0.05}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11 }}
                       tickFormatter={v => v ? new Date(v).toLocaleDateString(undefined, { month: 'short', year: '2-digit' }) : ''} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip
                  formatter={v => `${Number(v).toFixed(2)} tCO2e`}
                  labelFormatter={v => v ? new Date(v).toLocaleDateString(undefined, { month: 'long', year: 'numeric' }) : ''}
                />
                <Area type="monotone" dataKey="tco2e" stroke="#059669" fill="url(#emissionGrad)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart hint="Run Calculate emissions on the Emissions tab to populate the trend." />
          )}
        </div>

        {/* Scope donut */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Scope mix</h3>
          {byScope.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={byScope} dataKey="tco2e" nameKey="scope" innerRadius={50} outerRadius={80}
                     label={({ scope }) => scope}>
                  {byScope.map((s, i) => (
                    <Cell key={i} fill={SCOPE_COLORS[s.scope] || '#9ca3af'} />
                  ))}
                </Pie>
                <Tooltip formatter={v => `${Number(v).toFixed(2)} tCO2e`} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart hint="No scope mix until emissions are calculated." />
          )}
        </div>
      </div>

      {/* Bottom row — data origins + counts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Records by category */}
        <div className="lg:col-span-2 bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Records by category</h3>
          {byOrigin.length > 0 ? (
            <ResponsiveContainer width="100%" height={140}>
              <PieChart>
                <Pie data={byOrigin} dataKey="row_count" nameKey="label" outerRadius={50}>
                  {byOrigin.map((o, i) => <Cell key={i} fill={o.color} />)}
                </Pie>
                <Tooltip formatter={(v, _, p) => [`${Number(v).toLocaleString()} records`, p.payload.label]} />
                <Legend layout="vertical" align="right" verticalAlign="middle" iconSize={9} wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart hint="Upload activity data to populate." compact />
          )}
        </div>

        {/* Goals + scenarios counts */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-emerald-50 rounded-md border border-emerald-200 p-4 flex flex-col justify-between">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-emerald-700">
              <Target className="w-4 h-4" />
              Active goals
            </div>
            <div className="text-3xl font-bold text-gray-900 mt-2">{stats?.active_goals ?? '—'}</div>
          </div>
          <div className="bg-blue-50 rounded-md border border-blue-200 p-4 flex flex-col justify-between">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-blue-700">
              <GitBranch className="w-4 h-4" />
              Active scenarios
            </div>
            <div className="text-3xl font-bold text-gray-900 mt-2">{stats?.active_scenarios ?? '—'}</div>
          </div>
        </div>
      </div>

      {a.pending > 0 && (
        <div className="mt-6 p-4 rounded-md bg-amber-50 border border-amber-200 text-sm text-amber-900 flex gap-3">
          <Sparkles className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div>
            <strong>{Number(a.pending).toLocaleString()} records pending calculation.</strong> Switch to the
            <em> Emissions</em> tab and click <em>Calculate emissions</em> to compute co2e for all pending records.
          </div>
        </div>
      )}

      {!a.pending && a.calculated === 0 && (a.total_records || 0) > 0 && (
        <div className="mt-6 p-4 rounded-md bg-blue-50 border border-blue-200 text-sm text-blue-900 flex gap-3">
          <Sparkles className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div>
            <strong>Activity data is loaded.</strong> Add at least one emission factor on the
            <em> Emissions</em> tab (e.g. <code className="px-1 bg-white rounded">electricity / India / 0.71 kgCO2e/kWh</code>),
            then click <em>Calculate emissions</em> to populate the trend chart and scope mix.
          </div>
        </div>
      )}
    </div>
  );
}

function EmptyChart({ hint, compact = false }) {
  return (
    <div className={`flex flex-col items-center justify-center text-center text-gray-400 ${compact ? 'py-4' : 'py-12'}`}>
      <BarChart3 className={`${compact ? 'w-6 h-6' : 'w-10 h-10'} text-gray-300 mb-2`} />
      <div className="text-xs">{hint}</div>
    </div>
  );
}
